import React, { useCallback, useRef } from 'react';
import { createUseStyles } from 'react-jss';
import { Tooltip, DescendingGraphIcon, Theme } from '@spoiler-alert/ui-library';
import { DesirabilityStrings } from '../../string-resources';
import Tracking from '../../utilities/tracking-apis';
import DesirabilityEvents from './desirability-events';
import { type Desirability, isDesirabilityFlagged } from './desirability-presenter';

const useStyles = createUseStyles(
  {
    tooltip: {
      color: Theme.white,
      backgroundColor: Theme.greyDark,
      fontFamily: 'Roboto, sans-serif',
      fontSize: 12,
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: 'normal',
      maxWidth: 250,
      borderRadius: 2,
    },
    icon: {
      display: 'inline-flex',
    },
  },
  { name: 'DesirabilityIndicator' },
);

interface DesirabilityIndicatorProps {
  desirability?: Desirability | null;
  /** Where the indicator is rendered, sent with the hover event (e.g. "trucklane-drilldown"). */
  surface: string;
}

const DesirabilityIndicator = ({ desirability, surface }: DesirabilityIndicatorProps) => {
  const classes = useStyles();
  const tracked = useRef(false);

  // Fire once per mount: a table row can be hovered repeatedly and we want viewers, not hovers.
  const trackEngagement = useCallback(() => {
    if (tracked.current) return;
    tracked.current = true;
    Tracking.Event(DesirabilityEvents.hoveredIcon, { surface });
  }, [surface]);

  if (!isDesirabilityFlagged(desirability)) return null;

  return (
    <Tooltip
      small
      bottom
      boxStyle={{ backgroundColor: Theme.greyDark, border: 'none', borderRadius: '2px' }}
      showArrow={false}
      text={<div className={classes.tooltip}>{DesirabilityStrings.heading}</div>}
    >
      <span className={classes.icon} data-testid="desirability-indicator" onMouseEnter={trackEngagement} onFocus={trackEngagement}>
        <DescendingGraphIcon />
      </span>
    </Tooltip>
  );
};

export default DesirabilityIndicator;
